// src/features/treinos/components/ExercicioEvolucaoOffcanvas.jsx
//
// Bottom-sheet de evolução por exercício. Stats computados na LEITURA do
// WORKOUT_LOG; só overrides manuais (3 cards) são persistidos em EXERCICIO_STATS.
// Reusa o gráfico react-chartjs-2 (EvolucaoExercicio) e o Epley (via aggregate).
//
// Shell próprio (position:fixed) com drag handle de 3 detents (FULL/PEEK/FECHADO)
// via Pointer Events — sem libs (substitui o react-bootstrap Offcanvas porque o
// transform interno dele conflitava com o translateY do arraste).

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { OverlayTrigger, Popover } from "react-bootstrap";
import {
  FaPencilAlt,
  FaCheck,
  FaTimes,
  FaInfoCircle,
  FaTrophy,
  FaHistory,
  FaBullseye,
  FaRedo,
  FaWeightHanging,
  FaDumbbell,
  FaBolt,
  FaFire,
  FaPercentage,
} from "react-icons/fa";
import EvolucaoExercicio from "./EvolucaoExercicio.jsx";
import { VideoThumb, VideoModal } from "./VideoExercicio.jsx";
import { useExerciseMedia } from "../hooks/useExerciseMedia.js";
import {
  fetchWorkoutSessions,
  getExercicioOverride,
  saveExercicioOverride,
  exercicioDocId,
} from "../services/exerciseStatsService.js";
import { aggregateExerciseStats } from "../utils/exerciseStats.js";
import { recomendarCarga, parseReps } from "../utils/loadRecommendation.js";
import Style from "../../../styles/features/treinos/ExercicioEvolucao.module.scss";

// Textos do "i" de info por stat (no topo, fáceis de tunar).
const INFO_TEXTS = {
  reps: "Total de repetições feitas na sua história neste exercício.",
  volume: "Soma de toda a carga levantada (carga × reps).",
  sessoes: "Quantidade de treinos onde esse exercício foi feito.",
  e1rm: "Maior força prevista para 1 repetição.",
  constancia: "Percentual de vezes que você concluiu este exercício.",
  streak: "Maior sequência sem pular este exercício.",
  pr: "Maior carga que você já levantou neste exercício.",
  ultima: "Carga usada na sua última sessão deste exercício.",
  recomendada: "Sugestão de carga para a próxima série, com base no seu histórico.",
};

const fmtKg = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? "—" : `${+(+v).toFixed(1)}`);

function InfoDot({ texto }) {
  return (
    <OverlayTrigger
      trigger={["hover", "focus", "click"]}
      placement="top"
      rootClose
      overlay={<Popover><Popover.Body className={Style.infoPop}>{texto}</Popover.Body></Popover>}
    >
      <button type="button" className={Style.infoDot} aria-label="Info" onClick={(e) => e.stopPropagation()}>
        <FaInfoCircle size={11} />
      </button>
    </OverlayTrigger>
  );
}

// Card editável (PR / última / recomendada): valor computado + override por lápis.
// Layout: coluna esq. (ícone em cima, título abaixo) · valor à direita ·
// lápis no canto inferior direito (absolute) · "i" no canto superior direito.
function EditableCard({ icon, label, value, unit, info, onSave, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const begin = () => { setDraft(value == null ? "" : String(value)); setEditing(true); };
  const commit = () => {
    const n = parseFloat(String(draft).replace(",", "."));
    if (Number.isFinite(n) && n >= 0) onSave(n);
    setEditing(false);
  };

  return (
    <div className={Style.editCard}>
      <div className={Style.editCardLeft}>
        <span className={Style.editCardIcon}>{icon}</span>
        <span className={Style.editCardLabel}>{label}</span>
      </div>

      {info && <div className={Style.editCardInfo}><InfoDot texto={info} /></div>}

      {editing ? (
        <div className={Style.editRow}>
          <input
            className={Style.editInput}
            type="number"
            inputMode="decimal"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          />
          <button type="button" className={Style.editOk} onClick={commit} aria-label="Salvar"><FaCheck size={11} /></button>
          <button type="button" className={Style.editCancel} onClick={() => setEditing(false)} aria-label="Cancelar"><FaTimes size={11} /></button>
        </div>
      ) : (
        <>
          <div className={Style.editValue}>
            <span className={Style.num}>{fmtKg(value)}</span>
            <span className={Style.unit}>{unit}</span>
          </div>
          {!readOnly && <button type="button" className={Style.editPencil} onClick={begin} aria-label={`Editar ${label}`}>
            <FaPencilAlt size={11} />
          </button>}
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value, unit, info }) {
  return (
    <div className={Style.statCell}>
      <div className={Style.statTop}>
        <span className={Style.statIcon}>{icon}</span>
        <span className={Style.statLabel}>{label}</span>
        <InfoDot texto={info} />
      </div>
      <div className={Style.statValue}>
        <span className={Style.num}>{value}</span>
        {unit && <span className={Style.unit}>{unit}</span>}
      </div>
    </div>
  );
}

export default function ExercicioEvolucaoOffcanvas({ show, onHide, uid, exercicio, perfil, readOnly = true, onAction, actionLabel, actionDisabled = false }) {
  const nome = exercicio?.nome || exercicio?.NOME || "Exercício";
  const exerciseId = exercicio?.EXERCISE_ID || exercicio?.exercise_id || null;
  const grupo = exercicio?.muscle_group || exercicio?.MUSCLE_GROUP || exercicio?.GRUPO_MUSCULAR;
  const repsAlvo = useMemo(() => parseReps(exercicio?.reps ?? exercicio?.REPS)?.value, [exercicio]);

  // Mídia (vídeo/thumbnail) resolvida da exercise_library por EXERCISE_ID — o
  // exercício aqui costuma vir só com id/nome (sem video_url inline).
  const mediaList = useMemo(() => (exercicio ? [exercicio] : []), [exercicio]);
  const getMedia = useExerciseMedia(mediaList);
  const media = getMedia(exercicio || {});
  const docId = useMemo(() => exercicioDocId({ exerciseId, nome }), [exerciseId, nome]);

  const [sessions, setSessions] = useState([]);
  const [override, setOverride] = useState(null);
  const [video, setVideo] = useState(null);

  useEffect(() => {
    setSessions([]); setOverride(null);
    if (!show || !uid) return;
    let alive = true;
    fetchWorkoutSessions(uid).then((s) => { if (alive) setSessions(s); });
    getExercicioOverride(uid, docId).then((o) => { if (alive) setOverride(o || {}); });
    return () => { alive = false; };
  }, [show, uid, docId]);

  const agg = useMemo(
    () => aggregateExerciseStats(sessions, { exerciseId, nome }),
    [sessions, exerciseId, nome],
  );

  const recomendadaCalc = useMemo(
    () => recomendarCarga({
      historicoExercicio: agg.historicoExercicio,
      repsAlvo,
      perfilUsuario: perfil,
      exercicio: { grupo },
    }),
    [agg, repsAlvo, perfil, grupo],
  );

  // valor exibido = override quando houver; senão computado.
  const ov = override || {};
  const prVal = ov.pr_override ?? (agg.prCarga || null);
  const ultimaVal = ov.ultima_override ?? (agg.ultimaCarga || null);
  const recVal = ov.recomendada_override ?? recomendadaCalc;

  const persist = useCallback(
    (field) => async (valor) => {
      setOverride((prev) => ({ ...(prev || {}), [field]: valor }));
      try {
        await saveExercicioOverride(uid, docId, {
          [field]: valor,
          manual_flags: { ...(ov.manual_flags || {}), [field]: true },
        });
      } catch { /* erro já logado no service */ }
    },
    [uid, docId, ov.manual_flags],
  );

  // ── Bottom-sheet: drag handle com 3 detents (FULL / PEEK / FECHADO) ──────────
  const [render, setRender] = useState(show);
  const [y, setY] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 1000));
  const [dragging, setDragging] = useState(false);

  const yRef = useRef(y);
  const detentRef = useRef("full");          // "full" | "peek"
  const dims = useRef({ full: 0, peekY: 0 }); // alturas medidas (px)
  const drag = useRef(null);
  const reduceMotion = useRef(false);
  const sheetRef = useRef(null);
  const peekRef = useRef(null);               // header + linha de cargas (medição peek)

  const applyY = useCallback((v) => { yRef.current = v; setY(v); }, []);

  const measure = useCallback(() => {
    const sh = sheetRef.current;
    if (!sh) return;
    const full = sh.offsetHeight;
    const pk = peekRef.current;
    const peekH = pk ? pk.offsetTop + pk.offsetHeight + 10 : 280; // fallback fixo
    dims.current = { full, peekY: Math.max(0, full - peekH) };
  }, []);

  useEffect(() => {
    reduceMotion.current =
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Monta ao abrir.
  useEffect(() => {
    if (show) setRender(true);
  }, [show]);

  // Scroll-lock do body enquanto montado.
  useLayoutEffect(() => {
    if (!render) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [render]);

  // Animação de entrada: mede e desliza pra FULL.
  useLayoutEffect(() => {
    if (!render || !show) return undefined;
    measure();
    detentRef.current = "full";
    applyY(dims.current.full || window.innerHeight); // off-screen instantâneo
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => applyY(0)),         // anima pra FULL
    );
    sheetRef.current?.focus?.();
    return () => cancelAnimationFrame(id);
  }, [render]);

  // Animação de saída quando o pai fecha (show=false).
  useEffect(() => {
    if (show || !render) return undefined;
    measure();
    setDragging(false);
    applyY(dims.current.full || window.innerHeight);
    const t = setTimeout(() => setRender(false), reduceMotion.current ? 0 : 280);
    return () => clearTimeout(t);
  }, [show, render]);

  // Snap pro detent mais próximo (25% da distância OU velocidade do gesto).
  const settle = useCallback((vy) => {
    const { full, peekY } = dims.current;
    const cur = yRef.current;
    const V = 0.6; // px/ms
    const goFull = () => { detentRef.current = "full"; applyY(0); };
    const goPeek = () => { detentRef.current = "peek"; applyY(peekY); };

    if (detentRef.current === "full") {
      if (vy > V) return cur > peekY * 1.5 ? onHide() : goPeek();
      if (vy < -V) return goFull();
      if (cur > peekY * 1.5) return onHide();
      if (cur > peekY * 0.25) return goPeek();
      return goFull();
    }
    // detent === "peek"
    if (vy < -V) return goFull();
    if (vy > V) return onHide();
    if (cur < peekY * 0.75) return goFull();
    if (cur > peekY + (full - peekY) * 0.25) return onHide();
    return goPeek();
  }, [applyY, onHide]);

  const onPointerDown = useCallback((e) => {
    measure();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    drag.current = { start: e.clientY, base: yRef.current, lastY: e.clientY, lastT: performance.now(), vy: 0 };
    setDragging(true);
  }, [measure]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const now = performance.now();
    let ny = d.base + (e.clientY - d.start);
    ny = Math.max(0, Math.min(ny, dims.current.full)); // segue o dedo, com limites
    const dt = now - d.lastT;
    if (dt > 0) { d.vy = (e.clientY - d.lastY) / dt; d.lastY = e.clientY; d.lastT = now; }
    applyY(ny);
  }, [applyY]);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    const vy = d.vy;
    drag.current = null;
    setDragging(false);
    settle(vy);
  }, [settle]);

  if (!render) {
    // VideoModal vive fora do sheet para sobreviver a PEEK/fechamento parcial.
    return (
      <VideoModal show={!!video} onHide={() => setVideo(null)} url={video?.url} url2={video?.url2} nome={video?.nome} />
    );
  }

  const full = dims.current.full || 1;
  const openRatio = Math.max(0, Math.min(1, 1 - y / full));
  const noTrans = dragging || reduceMotion.current;

  return createPortal(
    <div className={Style.root} role="dialog" aria-modal="true" aria-label={`Evolução de ${nome}`}>
      <div
        className={Style.backdrop}
        style={{ opacity: openRatio * 0.55, transition: noTrans ? "none" : undefined }}
        onClick={onHide}
      />

      <div
        ref={sheetRef}
        className={Style.sheet}
        tabIndex={-1}
        style={{ transform: `translateY(${y}px)`, transition: noTrans ? "none" : undefined }}
        onKeyDown={(e) => { if (e.key === "Escape") onHide(); }}
      >
        {/* Drag handle (toque + mouse via Pointer Events) */}
        <div
          className={Style.handle}
          role="separator"
          aria-label="Arraste para ajustar a altura"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className={Style.handleBar} />
        </div>

        {/* Zona PEEK: header + 3 cards (sempre visível em PEEK) — usada na medição */}
        <div ref={peekRef} className={Style.peekZone}>
          <div className={Style.header}>
            <div className={Style.headerTop}>
              <VideoThumb
                url={media.url}
                thumbnail={media.thumbnail}
                onClick={media.url ? () => setVideo({ url: media.url, url2: media.url2, nome }) : undefined}
              />
              <div className={Style.headerTexts}>
                <h3 className={Style.exName}>{nome}</h3>
                <span className={Style.exSub}>Evolução do exercício</span>
              </div>
              <button type="button" className={Style.closeBtn} onClick={onHide} aria-label="Fechar">
                <FaTimes size={16} />
              </button>
            </div>
          </div>

          {/* 3 cards editáveis */}
          <div className={Style.cards3}>
            <EditableCard readOnly={readOnly} icon={<FaTrophy size={14} />} label="Melhor carga (PR)" value={prVal} unit="kg" info={INFO_TEXTS.pr} onSave={persist("pr_override")} />
            <EditableCard readOnly={readOnly} icon={<FaHistory size={14} />} label="Última carga" value={ultimaVal} unit="kg" info={INFO_TEXTS.ultima} onSave={persist("ultima_override")} />
            <EditableCard readOnly={readOnly} icon={<FaBullseye size={14} />} label="Recomendada" value={recVal} unit="kg" info={INFO_TEXTS.recomendada} onSave={persist("recomendada_override")} />
          </div>
        </div>

        {/* Conteúdo que some no PEEK */}
        <div className={Style.scrollArea}>
          {media.url && <div className={Style.videoPreview} role="button" tabIndex={0} onKeyDown={e => { if(e.key === "Enter" || e.key === " ") { e.preventDefault(); setVideo({url:media.url,url2:media.url2,nome}); } }} onClick={() => setVideo({ url:media.url, url2:media.url2, nome })} aria-label={`Assistir execução de ${nome}`}>
            <VideoThumb url={media.url} thumbnail={media.thumbnail} width="100%" height="100%" />
          </div>}
          {grupo && <span className={Style.groupChip}>{grupo}</span>}
          {/* Gráfico de evolução (reuso + seletor "Últimas N") */}
          <div className={Style.chartCard}>
            <EvolucaoExercicio uid={uid} exerciseId={exerciseId} nome={nome} />
          </div>

          {/* 6 stats (computados do WORKOUT_LOG) */}
          <div className={Style.statsGrid}>
            <Stat icon={<FaRedo size={12} />} label="Reps totais" value={agg.repsTotais} info={INFO_TEXTS.reps} />
            <Stat icon={<FaWeightHanging size={12} />} label="Carga total" value={fmtKg(agg.cargaTotal)} unit="kg" info={INFO_TEXTS.volume} />
            <Stat icon={<FaDumbbell size={12} />} label="Sessões" value={agg.sessoes} info={INFO_TEXTS.sessoes} />
            <Stat icon={<FaBolt size={12} />} label="1RM estimado" value={fmtKg(agg.e1rmMax)} unit="kg" info={INFO_TEXTS.e1rm} />
            {agg.mostrarStreak && (
              <>
                <Stat icon={<FaPercentage size={12} />} label="Constância" value={`${Math.round((agg.constancia || 0) * 100)}`} unit="%" info={INFO_TEXTS.constancia} />
                <Stat icon={<FaFire size={12} />} label="Melhor sequência" value={agg.bestStreak} info={INFO_TEXTS.streak} />
              </>
            )}
          </div>

          {Array.isArray(exercicio?.passos) && exercicio.passos.length > 0 && <section className={Style.chartCard}><h4>Como executar</h4><ol>{exercicio.passos.map((passo,i)=><li key={i}>{passo}</li>)}</ol></section>}
          <p className={Style.footer}>Dados atualizados automaticamente pelo treino ao vivo.</p>
        </div>
        {onAction && <div className={Style.actionFooter}><button className={Style.actionButton} disabled={actionDisabled} onClick={onAction}>{actionLabel}</button></div>}
      </div>

      <VideoModal show={!!video} onHide={() => setVideo(null)} url={video?.url} url2={video?.url2} nome={video?.nome} />
    </div>,
    document.body,
  );
}
